// src/api/DiscoverySettingsApi.js

import axios from 'axios';
import { handleApiError } from './handleApiError.js'; // Using the established error handler

const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

/**
 * Fetches the list of all countries for the dropdown selector.
 * @param {string} token - The user's Firebase auth token.
 * @returns {Promise<object>} - A promise that resolves to { status, data } or { status, message, error }.
 */
export const fetchAllCountries = async (token) => {
  const url = `${baseUrl}/settings/api/countries`;
  console.debug('[fetchAllCountries] Attempting to fetch country list...');
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    console.debug('[fetchAllCountries] Successfully fetched country list.');
    return { status: true, data: response.data }; // Expects array [{ value: 'US', label: 'United States' }]
  } catch (error) {
    return handleApiError(error, 'fetchAllCountries');
  }
};

/**
 * Fetches the current discovery settings (excluded countries and keywords).
 * @param {string} token - The user's Firebase auth token.
 * @returns {Promise<object>} - A promise that resolves to { status, data } or { status, message, error }.
 */
export const fetchDiscoverySettings = async (token) => {
  const url = `${baseUrl}/settings/api/discovery`;
  console.debug(
    '[fetchDiscoverySettings] Attempting to fetch current settings...',
  );
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    console.debug(
      '[fetchDiscoverySettings] Successfully fetched settings:',
      response.data,
    );
    return { status: true, data: response.data }; // Expects { excluded_countries: [...], description_keywords_text: "..." }
  } catch (error) {
    return handleApiError(error, 'fetchDiscoverySettings');
  }
};

/**
 * Updates the discovery settings on the backend.
 * @param {string} token - The user's Firebase auth token.
 * @param {object} settingsPayload - The settings to update, e.g., { excluded_countries: ['US'], description_keywords_text: 'email\ncontact' }.
 * @returns {Promise<object>} - A promise that resolves to { status, message } or { status, message, error }.
 */
export const updateDiscoverySettings = async (token, settingsPayload) => {
  const url = `${baseUrl}/settings/api/discovery`;
  console.debug(
    '[updateDiscoverySettings] Attempting to update settings with payload:',
    settingsPayload,
  );
  try {
    const response = await axios.put(url, settingsPayload, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    console.debug('[updateDiscoverySettings] Successfully updated settings.');
    return { status: true, message: response.data.message };
  } catch (error) {
    return handleApiError(error, 'updateDiscoverySettings');
  }
};

// --- ADD TO THE BOTTOM OF src/api/DiscoverySettingsApi.js ---

/**
 * Calls the AI to generate a list of 1-word keywords based on company GTM.
 * @param {string} token - The user's Firebase auth token.
 * @param {number} numberOfKeywords - The requested number of keywords (50-150).
 * @returns {Promise<object>} - A promise that resolves to { status, data } or { status, message, error }.
 */
export const generateDiscoveryKeywords = async (token, numberOfKeywords) => {
  const url = `${baseUrl}/settings/api/discovery/generate-keywords`;
  console.debug(
    `[generateDiscoveryKeywords] Requesting ${numberOfKeywords} keywords from AI...`,
  );
  try {
    const response = await axios.post(
      url,
      { number_of_keywords: numberOfKeywords },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      },
    );
    console.debug(
      '[generateDiscoveryKeywords] Successfully generated keywords.',
    );
    return { status: true, data: response.data.data };
  } catch (error) {
    return handleApiError(error, 'generateDiscoveryKeywords');
  }
};
